import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { HashRouter, Route, Routes } from 'react-router';
import { config } from './config';
import { main } from './configuration';
import { App } from './features/app/components/App';
import DefaultRedirect from './features/app/components/DefaultRoute';
import { ErrorBoundary } from './features/app/components/ErrorBoundary';
import './configuration/setup/datadog';
import type { User } from 'oidc-client-ts';
import { handleSilentSigninRedirectInIframe } from './configuration/login/redirect';
import { store } from './configuration/setup/store';
import { InvalidLogin } from './features/app/components/InvalidLogin';
import ErrorFallback from './features/app/components/ErrorFallback';

const redirectUsersUsingOldBookmark = () => {
    if (window.location.href.indexOf('/#assets') > -1) {
        window.location.href = window.location.href.replace('/#assets', '/#/assets');
    }
};

function renderApplication(user: User | null | undefined) {
    redirectUsersUsingOldBookmark();

    const container = document.getElementById('root');
    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
    const root = createRoot(container!);

    let showApp = true;
    try {
        showApp = user ? user.profile.sub.startsWith('prod-rio-users:') : true;
    } catch (e) {
        console.error(e);
    }

    if (showApp) {
        root.render(
            <React.StrictMode>
                <ErrorBoundary fallback={<ErrorFallback />}>
                    <Provider store={store}>
                        <HashRouter>
                            <Routes>
                                <Route path='*' element={<DefaultRedirect />} />
                                <Route path='/assets' element={<App />} />
                                <Route path='/assets/:id' element={<App />} />
                            </Routes>
                        </HashRouter>
                    </Provider>
                </ErrorBoundary>
            </React.StrictMode>
        );
    } else {
        root.render(<InvalidLogin />);
    }
}

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;
const isCypress = import.meta.env.VITE_CYPRESS_TEST_RUNTIME;

if ((isDev && config.enableMockServer) || isProdPreview) {
    if (isCypress) {
        import('../mocks/browser')
            .then(({ startWorker }) => startWorker({ forCypress: true }))
            .then(() => main(renderApplication));
    } else {
        import('../mocks/browser')
            .then(({ startWorker }) => startWorker({ forCypress: false }))
            .then(() => main(renderApplication));
    }
}

const isRedirect = window.location.href.startsWith(config.login.redirectUri as string);
const runsInIframe = 'parent' in window && window.parent !== window;

if (isRedirect && runsInIframe) {
    void handleSilentSigninRedirectInIframe();
} else if (isProd && !isProdPreview) {
    main(renderApplication);
}
